// Constants
import { SILENT_TOKEN_REFRESH_SCOPES, isProd } from "@/config/constant";

import { sfdcValidationTypes } from "@/config/sfdcConstant";
import * as msal from "@azure/msal-browser";

// Helpers
import {
  compareAsc,
  startOfToday,
  addDays,
  subDays,
  differenceInCalendarDays,
  format,
  isWeekend,
  eachHourOfInterval,
  getMinutes,
  isBefore,
} from "date-fns";
import { PublicClientApplication } from "@azure/msal-browser";

export const sleep = (timeout) =>
  new Promise((resolve) => setTimeout(resolve, timeout));

export const groupBy = (data, groupByKey) => {
  if (!data.length || !Array.isArray(data)) return null;

  const groupedData = data.reduce((prev, curr) => {
    if (!prev[curr[groupByKey]]) prev[curr[groupByKey]] = [];
    prev[curr[groupByKey]].push(curr);
    return prev;
  }, {});
  return groupedData;
};

export const converArrToFilerOptions = (arr) => {
  return arr.map((i) => ({
    label: i,
    value: i,
  }));
};

export const checkIsIOSSafari = () => {
  const ua = window.navigator.userAgent;
  const iOS = !!ua.match(/iPad/i) || !!ua.match(/iPhone/i);
  const webkit = !!ua.match(/WebKit/i);
  const iOSSafari = iOS && webkit && !ua.match(/CriOS/i);
  return iOSSafari;
};

export const isEmpty = (obj) => {
  for (let prop in obj) {
    // if (obj["hasOwnProperty"](prop)) return false;
    if (Object.prototype.hasOwnProperty.call(obj, prop)) return false;
  }
  return true;
};

export const isObject = (obj) => {
  return !!obj && obj.constructor === Object;
};

export const isArray = (arr) => {
  return !!arr && arr.constructor === Array;
};

export function debounce(func) {
  var wait =
    arguments.length <= 1 || arguments[1] === undefined ? 100 : arguments[1];

  var timeout = void 0;
  return function () {
    var _this = this;

    for (
      var _len = arguments.length, args = Array(_len), _key = 0;
      _key < _len;
      _key++
    ) {
      args[_key] = arguments[_key];
    }

    clearTimeout(timeout);
    timeout = setTimeout(function () {
      func.apply(_this, args);
    }, wait);
  };
}

export function getRange(startAt = 0, end) {
  return [...Array(end).keys()].map((i) => i + startAt);
}

export function generatePaginationConfiguration(selected, pageCount) {
  const config = {
    left: [],
    right: [],
    breakView: null,
  };
  const items = [];
  const pageRangeDisplayed = 2;
  const marginPagesDisplayed = 3;
  const breakLabel = "...";

  if (pageCount <= pageRangeDisplayed) {
    for (let index = 0; index < pageCount; index++) {
      items.push(index);
      config.left.push(index);
    }
  } else {
    let leftSide = pageRangeDisplayed / 2;
    let rightSide = pageRangeDisplayed - leftSide;

    // If the selected page index is on the default right side of the pagination,
    // we consider that the new right side is made up of it (= only one break element).
    // If the selected page index is on the default left side of the pagination,
    // we consider that the new left side is made up of it (= only one break element).
    if (selected > pageCount - pageRangeDisplayed / 2) {
      rightSide = pageCount - selected;
      leftSide = pageRangeDisplayed - rightSide;
    } else if (selected < pageRangeDisplayed / 2) {
      leftSide = selected;
      rightSide = pageRangeDisplayed - leftSide;
    }

    let index;
    let page;
    let breakView;

    for (index = 0; index < pageCount; index++) {
      page = index + 1;

      // If the page index is lower than the margin defined,
      // the page has to be displayed on the left side of
      // the pagination.
      if (page <= marginPagesDisplayed) {
        items.push(index);
        config.left.push(index);
        continue;
      }

      // If the page index is greater than the page count
      // minus the margin defined, the page has to be
      // displayed on the right side of the pagination.
      if (page > pageCount - marginPagesDisplayed) {
        //   items.push(createPageView(index));
        items.push(index);
        config.right.push(index);
        continue;
      }

      // If the page index is near the selected page index
      // and inside the defined range (pageRangeDisplayed)
      // we have to display it (it will create the center
      // part of the pagination).
      if (index >= selected - leftSide && index <= selected + rightSide) {
        items.push(index);
        config.left.push(index);
        continue;
      }

      // If the page index doesn't meet any of the conditions above,
      // we check if the last item of the current "items" array
      // is a break element. If not, we add a break element, else,
      // we do nothing (because we don't want to display the page).
      if (breakLabel && items[items.length - 1] !== breakView) {
        items.push(breakView);
        breakView = "true";
        config.breakView = breakView;
        config.breakViewIndex = index;
      }
    }
  }
  return config;
}

/**
 * @description function is used by getMsalAccessToken & getMsalIdToken only when token is expired and new token is required
 * @param {*} vm
 * @returns tokens with access and id token
 */
export async function refreshToken(vm, requestScope, key) {
  const msalLibrary = new PublicClientApplication(vm.$store.state.msalConfig);
  const {
    idTokenClaims: { email: username },
  } = await vm.$store.getters.getTokenData;
  const currentAccount = msalLibrary.getAccountByUsername(username);
  const tokens = await msalLibrary
    .acquireTokenSilent(requestScope(currentAccount))
    .then((resp) => {
      if (resp !== null) {
        const { idToken, accessToken } = resp;
        if (key === "msal_token") {
          vm.$store.dispatch("setAccessTokenAction", resp);
        }
        sessionStorage.setItem(key, JSON.stringify(resp));
        return {
          idToken,
          accessToken,
        };
      }
    })
    .catch(function (error) {
      console.error(error);
    });
  return tokens;
}

/**
 * @description This returns the MSAL Access Token, if the token is expired then calls refreshToken()
 * @param {*} vm
 * @returns the MSAL Access Token
 */

export async function getMsalToken(vm) {
  let { expiresOn } = JSON.parse(sessionStorage.getItem("msal_token"));
  // Validate if the token is not expired or not
  if (compareAsc(new Date(expiresOn), new Date()) === -1) {
    const { accessToken } = await refreshToken(vm);
    return accessToken;
  } else {
    let { accessToken } = JSON.parse(sessionStorage.getItem("msal_token"));
    return accessToken;
  }
}

/**
 * @description This returns the MSAL Access Token, if the token is expired then calls refreshToken()
 * @param {*} vm
 * @returns the MSAL Access Token
 */
export async function getMsalAccessToken(vm) {
  let { expiresOn } = JSON.parse(sessionStorage.getItem("msal_token"));
  // Validate if the token is not expired or not
  if (compareAsc(new Date(expiresOn), new Date()) === -1) {
    const { accessToken } = await refreshToken(
      vm,
      SILENT_TOKEN_REFRESH_SCOPES,
      "msal_token"
    );
    return accessToken;
  } else {
    let { accessToken } = JSON.parse(sessionStorage.getItem("msal_token"));
    return accessToken;
  }
}

/**
 * @description This returns the MSAL ID Token, if the token is expired then calls refreshToken()
 * @param {*} vm
 * @returns the MSAL ID Token
 */
export async function getMsalIdToken(vm) {
  let { expiresOn } = JSON.parse(sessionStorage.getItem("msal_token"));
  // Validate if the token is not expired or not
  if (compareAsc(new Date(expiresOn), new Date()) === -1) {
    const { idToken } = await refreshToken(
      vm,
      SILENT_TOKEN_REFRESH_SCOPES,
      "msal_token"
    );
    return idToken;
  } else {
    let { idToken } = JSON.parse(sessionStorage.getItem("msal_token"));
    return idToken;
  }
}

export async function getMsalTokenSilently(vm, request) {
  const msalLibrary = new PublicClientApplication(vm.$store.state.msalConfig);
  const currentAccounts = msalLibrary.getAllAccounts();
  const accountId = currentAccounts[0].homeAccountId;
  const currentAcc = msalLibrary.getAccountByHomeId(accountId);
  request.account = currentAccounts[0];
  if (currentAcc) {
    return await msalLibrary
      .acquireTokenSilent(request)
      .catch(async (error) => {
        console.log("silent token acquisition fails.");
        if (error instanceof msal.InteractionRequiredAuthError) {
          console.log("acquiring token using redirect");
          msalLibrary.acquireTokenRedirect(request);
        } else {
          console.error(error);
        }
      });
  }
  return {};
}

export const getRandomNumber = () => {
  return Math.floor(Math.random() * 100 + 1);
};

export const scrollWindow = () => {
  window.scrollTo(0, 0);
};

export const checkIfDuplicateExists = (arr) => {
  if (!arr?.length) return false;
  return new Set(arr).size !== arr.length;
};

export const getWindowWidth = () => {
  return window.innerWidth;
};

export const scrollToElement = (targetElement, offset) => {
  let pos = targetElement.style.position;
  let top = targetElement.style.top;
  targetElement.style.position = "relative";
  targetElement.style.top = offset || "-20px";
  targetElement.scrollIntoView({ behavior: "smooth", block: "start" });
  targetElement.style.top = top;
  targetElement.style.position = pos;
};

export const getWindowHeight = () => {
  return Math.max(
    document.documentElement.clientHeight || 0,
    window.innerHeight || 0
  );
};

export const textSearchOnArrayOfObjectsBasedOnKey = (
  items,
  text,
  objectKey
) => {
  text = text.toLowerCase().split(" ");
  return items.filter(function (item) {
    return text.every(function (el) {
      return item[`${objectKey}`].toLowerCase().indexOf(el) > -1;
    });
  });
};

export const filterGroupFlatten = (configuration) => {
  if (!isObject(configuration)) return [];

  const items = Object.values(configuration);

  let filterList = [];

  items.forEach((i) => {
    if (
      i[0] &&
      (i[0]?.type === "RANGE" ||
        i[0]?.type === "RELATIVE_DATE_RANGE" ||
        i[0].values?.length)
    ) {
      filterList.push(i[0]);
    }
  });
  return filterList;
};

export function deepCopy(obj) {
  if (typeof obj !== "object" || obj === null) {
    return obj;
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime());
  }

  if (obj instanceof Array) {
    return obj.reduce((arr, item, i) => {
      arr[i] = deepCopy(item);
      return arr;
    }, []);
  }

  if (obj instanceof Object) {
    return Object.keys(obj).reduce((newObj, key) => {
      newObj[key] = deepCopy(obj[key]);
      return newObj;
    }, {});
  }
}

export const formatToISOString = (timestamp) =>
  timestamp.toISOString().replace(/.\d+Z$/g, "Z");

export const formatDateFromGivenDay = (selectedDate, relativeDate) =>
  typeof selectedDate === "number"
    ? selectedDate === 0
      ? startOfToday()
      : selectedDate >= 1
      ? addDays(relativeDate, selectedDate)
      : subDays(relativeDate, Math.abs(selectedDate))
    : null;

export const formatToRelativeDate = (date, relativeDateLocal) => {
  const differenceInDays = differenceInCalendarDays(date, relativeDateLocal);
  const dateABS = Math.abs(differenceInDays);
  const dateSuffix = dateABS > 1 ? "days" : "day";
  return differenceInDays === 0
    ? "Today"
    : differenceInDays >= 1
    ? `${dateABS} ${dateSuffix} from today`
    : `${dateABS} ${dateSuffix} ago`;
};

export const resetRelativeDateOnGivenFilter = (filterJSON) => {
  Object.keys(filterJSON).map((key) => {
    if (
      filterJSON[key] &&
      filterJSON[key][0] &&
      filterJSON[key][0].relative_date
    ) {
      filterJSON[key][0].relative_date = formatToISOString(startOfToday());
    }
  });
  return filterJSON;
};

export const resolutionDropdownFields = (data, fromConstantApi) => {
  let constantFields = Object.keys(data);
  let disputeConstantsFields = {};
  constantFields.forEach((key) => {
    const results = fromConstantApi ? data[key] : data[key].results;
    disputeConstantsFields[key] = resolutionDropdownMapping(
      results,
      key,
      fromConstantApi
    );
    disputeConstantsFields[key] = [
      {
        id: "select",
        displayName: "Select",
        label: "Select",
        value: "",
      },
      ...disputeConstantsFields[key],
    ];
  });
  return disputeConstantsFields;
};

export const resolutionDropdownMapping = (results, key, fromConstantApi) => {
  return Object.entries(results).map(([index, value]) => {
    return {
      id: index,
      value: fromConstantApi
        ? value
        : key === "STATUS"
        ? `${index.split("_")[0]}: ${value}`
        : key === "CURRENCY"
        ? index
        : key === "CHARGE_CODE"
        ? `${index.split("-")?.[0]}: ${value}`
        : `${index}: ${value}`,
      displayName: value,
      label:
        key === "CHARGE_CODE"
          ? `${index.split("-")?.[0]}: ${value}`
          : key === "CURRENCY"
          ? index
          : `${index}: ${value}`,
      disputeCategoryName: key,
    };
  });
};

export const getZonedTime = (date) => {
  if (!date) {
    return null;
  }
  const dateSplit = date.split("T");
  return new Date(`${dateSplit[0]}T18:30:00Z`);
};

export const formatDate = (date, element) => {
  try {
    date =
      date[date.length - 1] === "Z"
        ? date
        : date.indexOf("T") > -1
        ? `${date}Z`
        : getZonedTime(date);
    return date
      ? element?.format
        ? format(new Date(`${date}`), "dd-MMM-yyyy")
        : format(new Date(`${date}`), "dd-MMM-yyyy hh:mm a")
      : "N/A";
  } catch (err) {
    return date;
  }
};
export function getFilterStatus(appliedFiltersByRole) {
  let clearFilter = true;
  Object.keys(appliedFiltersByRole).forEach((filter) => {
    if (
      (isObject(appliedFiltersByRole[filter]) &&
        !isEmpty(appliedFiltersByRole[filter])) ||
      (typeof appliedFiltersByRole[filter] === "boolean" &&
        appliedFiltersByRole[filter]) ||
      appliedFiltersByRole[filter].length
    ) {
      clearFilter = false;
    }
  });
  return clearFilter;
}
export const copyToClipboardDebounced = debounce(async function (
  value,
  showingToast,
  showToast,
  copyText
) {
  copyText(value);
  if (!showingToast) {
    showingToast = true;
    showToast("success", "Copied to Clipboard");
    setTimeout(() => {
      showingToast = false;
    }, 1000);
  }
},
500);

/**
 * @param {*} token (jwtToken)
 * @returns decoded object
 */
export const parseJwt = (token) => {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch (e) {
    return null;
  }
};

export const getCaseAging = (row) => {
  const lastDisputeTs = row?.lastDisputeTs;
  const releaseDate = isProd ? new Date("10/13/2021") : new Date("10/04/2021");
  if (lastDisputeTs && isBefore(new Date(lastDisputeTs), releaseDate)) {
    return null;
  }
  const startDate = row?.lastDisputeTs
    ? new Date(row.lastDisputeTs)
    : row?.createdTs
    ? new Date(row.createdTs)
    : null;
  const endDate = row?.closedTs ? new Date(row.closedTs) : new Date();
  if (!startDate || !(startDate <= endDate)) {
    return null;
  }
  return workingHoursBetweenDates(startDate, endDate, "caseAging");
};

const workingHoursBetweenDates = (startDate, endDate, key) => {
  let aging = eachHourOfInterval({
    start: startDate,
    end: endDate,
  }).filter((el) => !isWeekend(el)).length;
  const startMinutes = getMinutes(startDate);
  const endMinutes = getMinutes(endDate);
  aging = parseInt((aging - 1) * 60 + parseInt(endMinutes - startMinutes)) / 60;
  return addCaseAgingKeys(aging, key);
};

const addCaseAgingKeys = (aging, key) => {
  const hoursDiff = parseInt(aging % 24);
  if (aging > 24) {
    return {
      [key]: `${parseInt(aging / 24)} days ${
        hoursDiff > 1
          ? `${hoursDiff} hours`
          : hoursDiff === 1
          ? `${hoursDiff} hour`
          : ""
      }`,
      [`${key}Status`]: "CRITICAL",
    };
  }
  if (aging >= 20 && aging < 24) {
    return {
      [key]: `${hoursDiff} hours`,
      [`${key}Status`]: "HIGH",
    };
  }
  if (aging < 1) {
    return {
      [key]: `less than an hour`,
      [`${key}Status`]: "MEDIUM",
    };
  }
  if (aging < 20) {
    return {
      [key]: `${parseInt(aging)} hours`,
      [`${key}Status`]: "LOW",
    };
  }
};
export const replaceNewLineWithBr = (text) => {
  return text.replace(/(?:\r\n|\r|\n)/g, "<br>");
};

export const objectPick = (array = [], keys_to_keep = []) => {
  return array.map((o) =>
    keys_to_keep.reduce((acc, curr) => {
      acc[curr] = o?.[curr] || "";
      return acc;
    }, {})
  );
};

export const mergeArrayObjects = (arr1 = [], arr2 = [], id) => {
  return arr1.map((item, i) => {
    if (item?.[id] === arr2[i]?.[id]) {
      return Object.assign({}, item, arr2[i]);
    }
  });
};

export const validateEmail = (email) => {
  return String(email)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

export const validateSfdcRow = (
  row = {},
  col = {},
  type,
  { disputeReasons = [] }
) => {
  let hasError = false;
  let invalidItemCount = 0;
  let invalidItems = [];
  col?.validateColumns?.forEach((validateItem) => {
    if (sfdcValidationTypes?.[validateItem]?.validationType === "select") {
      const index = disputeReasons?.findIndex(
        (item) =>
          item?.value?.toLowerCase() === row[validateItem]?.toLowerCase()
      );
      if (index === -1) {
        hasError = true;
        invalidItemCount = invalidItemCount + 1;
        invalidItems.push(validateItem);
      }
    } else if (
      sfdcValidationTypes?.[validateItem]?.validationType === "email"
    ) {
      if (!row[validateItem]?.length || !validateEmail(row[validateItem])) {
        hasError = true;
        invalidItemCount = invalidItemCount + 1;
        invalidItems.push(validateItem);
      }
    } else if (
      sfdcValidationTypes?.[validateItem]?.validationType === "input" ||
      sfdcValidationTypes?.[validateItem]?.validationType === "textArea"
    ) {
      const isLengthInvalid =
        !row[validateItem]?.length ||
        row[validateItem]?.length >
          sfdcValidationTypes?.[validateItem]?.maxValue ||
        row[validateItem]?.length <
          sfdcValidationTypes?.[validateItem]?.minValue;

      const hasCustomValidation =
        sfdcValidationTypes?.[validateItem]?.customValidation;
      const isCustomValidationFailed =
        hasCustomValidation &&
        !sfdcValidationTypes?.[validateItem]?.customValidation(
          row[validateItem]
        );

      if (isLengthInvalid || isCustomValidationFailed) {
        hasError = true;
        invalidItemCount = invalidItemCount + 1;
        invalidItems.push(validateItem);
      }
    }
  });
  return type === "tooltip"
    ? invalidItems
    : type === "validateText"
    ? hasError
      ? `Validation failed for ${invalidItemCount} fields`
      : `Validation successful`
    : hasError;
};
export const convertHtmlToText = (value) => {
  const commentHTML = document.createElement("p");
  commentHTML.innerHTML = value;
  return commentHTML.textContent.trim();
};

export const showResolutionTime = (data) => {
  if (data.resolutionTAT) {
    return true;
  }
  return false;
};

export const excelDateToJSDate = (date) => {
  let converted_date = new Date(Math.round((date - 25569) * 864e5));
  converted_date = String(converted_date).slice(4, 15);
  date = converted_date.split(" ");
  let day = date[1];
  let month = date[0];
  month = "JanFebMarAprMayJunJulAugSepOctNovDec".indexOf(month) / 3 + 1;
  if (month.toString().length <= 1) month = "0" + month;
  let year = date[2];
  return String(day + "-" + month + "-" + year);
};

export function sortCategoryOptionsByType(categoryOptions = []) {
  const orderMap = {
    Accepted: 1,
    Rejected: 2,
    Redispute: 3,
  };

  const selectOption = categoryOptions.find((opt) => opt.value === "");
  const otherOptions = categoryOptions.filter((opt) => opt.value !== "");

  const sortedOptions = otherOptions.sort((a, b) => {
    const aType = a.value.split(" - ")[0];
    const bType = b.value.split(" - ")[0];

    return (orderMap[aType] || 99) - (orderMap[bType] || 99);
  });
  return selectOption ? [selectOption, ...sortedOptions] : sortedOptions;
}
